import { useCallback, useRef } from 'react'
import { calculateReconnectDelay, RECONNECTION_CONFIG } from '@/infra'

export interface ReconnectionState {
  readonly attemptCount: number
  readonly isMaxAttemptsReached: boolean
}

export interface UseStreamReconnectionReturn {
  readonly scheduleReconnection: (onReconnect: () => void) => void
  readonly cancelReconnection: () => void
  readonly resetAttempts: () => void
  readonly getAttemptCount: () => number
  readonly isMaxAttemptsReached: () => boolean
  readonly incrementAttempts: () => number
}

export function useStreamReconnection(): UseStreamReconnectionReturn {
  const attemptCountRef = useRef(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cancelReconnection = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const resetAttempts = useCallback(() => {
    attemptCountRef.current = 0
    cancelReconnection()
  }, [cancelReconnection])

  const getAttemptCount = useCallback(() => {
    return attemptCountRef.current
  }, [])

  const isMaxAttemptsReached = useCallback(() => {
    return attemptCountRef.current >= RECONNECTION_CONFIG.maxAttempts
  }, [])

  const incrementAttempts = useCallback(() => {
    attemptCountRef.current += 1
    return attemptCountRef.current
  }, [])

  const scheduleReconnection = useCallback(
    (onReconnect: () => void) => {
      cancelReconnection()

      if (isMaxAttemptsReached()) {
        return
      }

      const delay = calculateReconnectDelay(attemptCountRef.current)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        onReconnect()
      }, delay)
    },
    [cancelReconnection, isMaxAttemptsReached],
  )

  return {
    scheduleReconnection,
    cancelReconnection,
    resetAttempts,
    getAttemptCount,
    isMaxAttemptsReached,
    incrementAttempts,
  }
}
